import React, { useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import NextButton from './buttons/NextButton';

import ExclamationMark from '../assets/images/exclamationMark.svg';
import { BSError } from '../model/error/BSError';
import { toastState, ToastError, errorState, notificationState } from '../model/state/errorState';

interface ErrorPageProps {
  error: BSError;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ error }) => {
  return (
    <div className="flex h-full flex-1 items-center justify-center">
      <div className="w-[280px] rounded-[13px] pb-[30px] pl-[27px] pr-[27px] pt-[27px] text-black shadow-custom lg:w-[424px]">
        <h1 className="text-center align-middle font-RobotoFlex text-lg font-bold">
          {error.userFriendlyTitle}
        </h1>
        <p className="text-center font-RobotoFlex text-lg">{error.userFriendlyExplanation}</p>
      </div>
    </div>
  );
};
interface ToastProps {
  error: ToastError;
  onReset: () => void;
}

const Toast: React.FC<ToastProps> = ({ error, onReset }) => {
  const errorMessage = error.text;
  const [visualShow, setVisualShow] = useState(false);
  useEffect(() => {
    setVisualShow(false);
    setTimeout(() => {
      setVisualShow(true);
      setTimeout(() => {
        setTimeout(() => {
          onReset();
        }, 500);
        setVisualShow(false);
      }, 2000);
    }, 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <div className="absolute bottom-[120px] left-0 w-full">
      <div
        className={`mx-auto my-0 flex w-[343px] items-center gap-[11px] rounded-2xl bg-white p-4 shadow-customNotification transition-all duration-500 ${
          visualShow ? '' : 'translate-y-[200px]'
        }`}
      >
        <img alt="toast message" src={ExclamationMark} />
        <p className="m-0">{errorMessage}</p>
      </div>
    </div>
  );
};

interface NotificationPopupProps {
  message: string;
  onClose: () => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({ message, onClose }) => {
  return (
    <div className="absolute bottom-0 left-0 right-0 top-0 z-[100] flex items-center justify-center">
      <div className="m-5 flex max-w-[487px] flex-col items-center justify-center gap-[15px] rounded-md bg-white px-6 pb-[25px] pt-[51px] shadow-customNotification lg:m-0">
        <p className="font-RobotoFlex text-base font-light text-defaultText">{message}</p>
        <div className="flex w-full items-center justify-center">
          <NextButton width="100px" text="Got it" onClick={onClose} buttonRounded={true} />
        </div>
      </div>
    </div>
  );
};

interface ErrorInterrupterProps {
  children: JSX.Element;
}

export const ErrorInterrupter: React.FC<ErrorInterrupterProps> = ({ children }) => {
  const [toast, setToast] = useRecoilState(toastState);
  const [notification, setNotification] = useRecoilState(notificationState);
  const error = useRecoilValue(errorState);
  const currentError = toast.length > 0 ? toast[0] : undefined;

  return (
    <>
      {typeof error !== 'undefined' ? <ErrorPage error={error} /> : children}
      {currentError && (
        <Toast
          onReset={() => {
            setToast((old) => old.slice(1));
          }}
          error={currentError}
        />
      )}
      {notification && (
        <NotificationPopup
          message={notification}
          onClose={() => {
            setNotification(undefined);
          }}
        />
      )}
    </>
  );
};
